import { type ReactElement } from "react";
import { ArrowDown, Target } from "lucide-react";
import type { CausalChainParams } from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";

export type CausalChainSelection =
  | { readonly kind: "problem" }
  | { readonly kind: "step"; readonly index: number };

/** Read from the observed problem toward underlying causes, not in time order. */
export function CausalChainWidget({
  data,
  fit = false,
  selection,
  onSelect,
}: WidgetBodyProps<CausalChainParams> & {
  fit?: boolean;
  selection?: CausalChainSelection | null;
  onSelect?: (selection: CausalChainSelection) => void;
}): ReactElement {
  if (!data.steps.length && data.problem === undefined)
    return (
      <div className="grid flex-1 place-items-center text-sm text-pb-text-muted">
        No 5 Whys analysis yet. Expand to add a problem or Why.
      </div>
    );
  const card = (
    body: string,
    label: string,
    target: CausalChainSelection,
    root = false,
  ) => {
    const problem = target.kind === "problem";
    const selected =
      selection?.kind === target.kind &&
      (target.kind === "problem" ||
        (selection.kind === "step" && selection.index === target.index));
    const className = cn(
      "flex w-full min-w-0 items-start gap-3 rounded-md border p-3 text-start text-sm text-pb-text transition-[box-shadow,border-color] duration-motion-state ease-motion-standard motion-reduce:transition-none",
      problem
        ? "border-pb-amber/40 bg-pb-amber-soft/40"
        : root
          ? "border-pb-green/40 bg-pb-green/10"
          : "border-pb-border bg-pb-card-elevated/60",
      selected && "shadow-glow-selected border-pb-teal/40",
      onSelect && "focus-ring",
    );
    const content = (
      <>
        {!problem && (
          <span className="shrink-0 rounded bg-pb-card-elevated px-2 py-1 text-sm text-pb-text-muted">
            {target.index + 1}
          </span>
        )}
        <span className="min-w-0 flex-1">
          {(problem ||
            label !==
              `Why ${target.kind === "step" ? target.index + 1 : ""}`) && (
            <span
              className={cn(
                "mb-1 block break-words font-medium",
                problem && "text-pb-amber-text",
              )}
            >
              {label}
            </span>
          )}
          <span className="block whitespace-pre-wrap break-words leading-relaxed [overflow-wrap:anywhere]">
            {body || "No explanation yet"}
          </span>
        </span>
        {root && (
          <span className="flex shrink-0 items-center gap-1 text-xs text-pb-green-text">
            <Target className="h-3 w-3" aria-hidden />
            Root marked
          </span>
        )}
      </>
    );
    return onSelect ? (
      <button
        type="button"
        data-testid="causal-chain-card"
        aria-pressed={selected}
        onClick={() => onSelect(target)}
        className={className}
      >
        {content}
      </button>
    ) : (
      <div data-testid="causal-chain-card" className={className}>
        {content}
      </div>
    );
  };
  return (
    <div
      data-testid="causal-chain-expanded"
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-1"
    >
      {data.problem !== undefined && (
        <>
          {card(data.problem, "Problem", { kind: "problem" })}
          {data.steps.length > 0 && <WhyArrow />}
        </>
      )}
      {data.steps.map((step, index) => (
        <div key={index} className="shrink-0">
          {card(step.body, step.label, { kind: "step", index }, step.isRoot)}
          {index < data.steps.length - 1 && <WhyArrow />}
        </div>
      ))}
      {fit && (
        <p className="mt-3 text-xs text-pb-text-muted">
          Root markers indicate a selection, not proof of causation.
        </p>
      )}
    </div>
  );
}
function WhyArrow(): ReactElement {
  return (
    <div
      aria-hidden
      className="flex h-7 shrink-0 items-center gap-2 ps-3 text-xs text-pb-text-muted"
    >
      <ArrowDown className="h-4 w-4" />
      Why?
    </div>
  );
}
